import React, { useEffect, useState, useRef, useCallback, FocusEvent } from 'react';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { LogoLink, NavLink, TopBanner } from '@/components';
import { cn, supportedLanguages } from '@/lib/utils';
import { AmericanFlag } from './Flags';
import { LanguageCodeType, LinkInterface } from '@/types';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Search, ChevronDown } from 'lucide-react';
import { Cross2Icon, TextAlignJustifyIcon } from '@radix-ui/react-icons';

// Shadcn UI components still used for mobile menu
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Accordion as BaseAccordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Interface for language data structure
interface Language {
  language: LanguageCodeType;
  href: string;
}

interface NavigationItem extends LinkInterface {
  child?: LinkInterface[];
}

// Props interface for Navbar component with all required properties
export interface NavbarProps {
  items: NavigationItem[];
  languageVersions: Language[];
  currentLanguage: string;
  languages: LanguageCodeType[];
  languageNotAvailableTitle?: string;
  languageNotAvailableBlurb?: string;
  bannerEnabled: boolean;
  bannerTag?: string;
  bannerText: string;
  includeBannerCta: boolean;
  bannerCta?: LinkInterface | null;
  bannerStartDateTime?: string;
  bannerEndDateTime?: string;
}

//
// Desktop Subnav (Accessible & Keyboard-Friendly)
//
// 1. Show child nav on mouse hover and focus.
// 2. Hide child nav on mouse leave or if focus is lost.
// 3. Use a ref array to check if focus moves outside a sub-menu.
//
const Navbar: React.FC<NavbarProps> = ({
  items,
  languageVersions,
  currentLanguage,
  languages,
  languageNotAvailableTitle,
  languageNotAvailableBlurb,
  bannerEnabled,
  bannerTag,
  bannerText,
  includeBannerCta,
  bannerCta,
  bannerStartDateTime,
  bannerEndDateTime,
}) => {
  // State for mobile menu open/close
  const [open, setOpen] = useState<boolean>(false);

  // Timer to control the top “sticky” bar replication on mobile
  const [timeoutCompleted, setTimeoutCompleted] = useState<boolean>(false);

  // Language selection
  const [selectedLanguage, setSelectedLanguage] = useState<string>(currentLanguage);

  const { toast } = useToast();

  // Add these new state/ref declarations near the other state declarations
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Add this ref near other refs to track the search trigger button
  const searchTriggerRef = useRef<HTMLDivElement>(null);

  // ---------- Helper Functions ---------- //
  // Get the href for a language code, adjusting for homepage and trailing slash
  const getHrefByLanguageCode = (code: LanguageCodeType): string | undefined => {
    const language = languageVersions.find((lang) => lang.language === code);
    if (language && language.href) {
      return language.href.replace('/homepage', '').replace(/\/?$/, '/'); // ensure single trailing slash or remove as needed
    }
    return undefined;
  };

  const handleLanguageChange = () => {
    setOpen(false);
    toast({
      title: languageNotAvailableTitle,
      description: languageNotAvailableBlurb,
      icon: '/images/warning-icon.svg',
    });
  };

  // Build current page link with language path
  const getLanguagePageLink = useCallback(
    (param?: string) => {
      const baseLink = selectedLanguage === 'en' ? '/' : `/${selectedLanguage}/`;
      return param ? `${baseLink}${param}` : baseLink;
    },
    [selectedLanguage],
  );

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const query = formData.get('search')?.toString().trim();
    if (query) {
      navigate(`/search?term=${encodeURIComponent(query)}`);
      setOpen(false);
    }
  };

  //
  // ---------- Desktop Subnav Handlers ---------- //
  //
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const submenuRefs = useRef<Array<HTMLElement | null>>([]);

  const handleMouseEnter = (index: number) => {
    setExpandedIndex(index); // Open the hovered submenu
    const activeElement = document.activeElement as HTMLElement;

    if (activeElement?.closest('li')) {
      activeElement.blur();
    }

    // If focus is on a parent <a> tag, blur it when hovering over another menu item
    if (activeElement && activeElement.tagName === 'a') {
      activeElement.blur();
    }
    setExpandedIndex(index);
  };

  const handleMouseLeave = () => setExpandedIndex(null);

  const handleFocus = (index: number) => setExpandedIndex(index);
  const handleBlur = (e: FocusEvent, index: number) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setExpandedIndex(null);
    }
  };

  useEffect(() => {
    const handleEscapePress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setExpandedIndex(null);
      }
    };

    document.addEventListener('keydown', handleEscapePress);

    return () => {
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const submenu = submenuRefs.current[index]?.querySelectorAll('a');
    const isOpen = expandedIndex === index;
    const liElement = submenuRefs.current[index];

    switch (e.key) {
      case 'ArrowDown':
        if (isOpen && submenu?.length) {
          e.preventDefault(); // Prevent screen scrolling
          const currentIndex = Array.from(submenu).indexOf(
            document.activeElement as HTMLAnchorElement,
          );
          const nextIndex = (currentIndex + 1) % submenu.length;
          submenu[nextIndex]?.focus();
        } else if (!isOpen) {
          // Open the submenu if it's not already open
          e.preventDefault();
          setExpandedIndex(index);
        }
        break;

      case 'ArrowUp':
        if (isOpen && submenu?.length) {
          e.preventDefault(); // Prevent screen scrolling
          const currentIndex = Array.from(submenu).indexOf(
            document.activeElement as HTMLAnchorElement,
          );
          const prevIndex = (currentIndex - 1 + submenu.length) % submenu.length;
          submenu[prevIndex]?.focus();
        }
        break;

      case 'ArrowRight':
        e.preventDefault();
        const nextMenuIndex = (index + 1) % items.length;
        submenuRefs.current[nextMenuIndex]?.querySelector('a')?.focus();
        setExpandedIndex(nextMenuIndex);
        break;

      case 'ArrowLeft':
        e.preventDefault();
        const prevMenuIndex = (index - 1 + items.length) % items.length;
        submenuRefs.current[prevMenuIndex]?.querySelector('a')?.focus();
        setExpandedIndex(prevMenuIndex);
        break;

      case 'Escape':
        e.preventDefault();
        setExpandedIndex(null);
        if (isOpen) {
          // Close the submenu and move focus to the parent menu item
          setExpandedIndex(null);
          const parentNavLink = submenuRefs.current[index]?.querySelector('a');
          parentNavLink?.focus();
        } else {
          // Remove focus entirely if submenu is already closed
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement?.closest('li') === liElement) {
            activeElement.blur();
          }
        }
        setExpandedIndex(null);
        break;

      case 'Tab':
        if (isOpen && submenu?.length) {
          const firstElement = submenu[0];
          const lastElement = submenu[submenu.length - 1];
          if (!e.shiftKey && document.activeElement === lastElement) {
            setExpandedIndex(null);
          } else if (e.shiftKey && document.activeElement === firstElement) {
            setExpandedIndex(null);
          }
        }
        break;

      default:
        break;
    }
  };

  // Search toggle
  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    // Focus the input when showing search
    if (!isSearchVisible) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  // Open search on enter or space
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSearch();
    }
    // Close search on escape
    if (e.key === 'Escape' && isSearchVisible) {
      setIsSearchVisible(false);
    }
  };

  // Close search on escape
  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsSearchVisible(false);
      // Return focus to search trigger button
      searchTriggerRef.current?.focus();
    }
  };

  // ---------- Mobile Behavior & Effects ---------- //
  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth >= 1024) {
        setOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to manage timeout state for menu animations
  useEffect(() => {
    setTimeoutCompleted(open);
  }, [open]);

  // Component for small ticket button used in mobile view
  const RendedSmallTicketButton: React.FC = () => (
    <div className='relative mr-4 h-full max-[375px]:mr-2'>
      <GatsbyLink
        to={getLanguagePageLink('tickets')}
        className='ease-bounce after:bg-stubbottom before:bg-stubbottom focus:after:bg-stubbottom-dark focus:before:bg-stubbottom-dark group relative flex h-full items-center whitespace-nowrap bg-yellow-500 px-3 py-1 transition-all duration-200 before:absolute before:left-0 before:right-0 before:top-[-7px] before:h-[7px] before:w-full before:rotate-180 before:content-[""] after:absolute after:bottom-[-7px] after:left-0 after:right-0 after:h-[7px] after:w-full after:content-[""] hover:mt-[-2.5%] hover:h-[110%] focus:mt-[-2.5%] focus:h-[110%] focus:bg-blue-950 focus:text-white focus:outline-1 max-[375px]:pl-2 max-[375px]:pr-2'
      >
        <span className='text-sm font-semibold transition-colors duration-200'>tickets</span>
      </GatsbyLink>
    </div>
  );

  // Component for sheet trigger button used in mobile menu
  const RendedSheetTrigger: React.FC = () => (
    <SheetTrigger asChild>
      <Button
        variant='ghost'
        className='focus:bg-zinc-450 focus-visible:bg-zinc-450 h-10 border border-zinc-200 px-[7px] py-3 hover:bg-transparent focus:outline-1 focus:ring-0 max-[375px]:h-8 max-[375px]:p-[5px]'
        aria-label={open ? 'Close mobile menu' : 'Open mobile menu'}
      >
        {open ? (
          <Cross2Icon className='h-6 w-6 max-[375px]:h-5 max-[375px]:w-5' />
        ) : (
          <TextAlignJustifyIcon className='h-6 w-6 max-[375px]:h-5 max-[375px]:w-5' />
        )}
        <span className='sr-only'>Toggle Menu</span>
      </Button>
    </SheetTrigger>
  );

  //
  // ---------- Main Render ----------
  //
  return (
    <header className='relative'>
      <TopBanner
        enabled={bannerEnabled}
        tag={bannerTag}
        text={bannerText}
        includeCta={includeBannerCta}
        cta={bannerCta}
        startDateTime={bannerStartDateTime}
        endDateTime={bannerEndDateTime}
      />
      <div className='font-halyarddisplay relative z-[100] mx-auto flex h-[75px] max-w-screen-xl flex-wrap items-center justify-between bg-white px-5 py-5 md:pl-6 md:pr-6 lg:py-0 lg:pr-0 xl:pl-0'>
        {/* Logo */}
        <LogoLink className='mt-1 focus:outline-offset-2 max-[400px]:h-[15px] sm:h-6 md:h-[26px] lg:h-[22px] xl:h-[26px]' />

        {/* Desktop Navigation */}
        <nav className='hidden lg:block'>
          <ul className='flex space-x-5'>
            {items?.map((item, index) => {
              const hasSubNav = item.child && item.child.length > 0;
              const isExpanded = expandedIndex === index;

              return (
                <li
                  key={`desktop-item-${index}`}
                  className='group relative'
                  onMouseEnter={() => handleMouseEnter(index)}
                  onMouseLeave={handleMouseLeave}
                  onFocus={() => handleFocus(index)}
                  onBlur={(e) => handleBlur(e, index)}
                  ref={(el) => {
                    if (el) submenuRefs.current[index] = el;
                  }}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                >
                  <NavLink
                    {...item}
                    ariaLabel={item.label}
                    className='squiggly-link font-base group relative inline-flex items-center whitespace-nowrap rounded-sm p-1 text-base font-semibold text-black focus:outline-2 focus:ring-blue-500 lg:text-lg xl:pb-1.5 xl:pt-1 xl:text-xl'
                    activeClassName='text-blue-600'
                    aria-expanded={hasSubNav ? isExpanded : undefined}
                    tabIndex={0}
                  >
                    {item.label}
                    {hasSubNav && (
                      <ChevronDown
                        className={cn(
                          'relative top-[1px] ml-1 h-4 w-4 text-black transition duration-200',
                          `${isExpanded ? 'rotate-180' : ' '}`,
                        )}
                        aria-hidden='true'
                      />
                    )}
                  </NavLink>
                  {/* Submenu */}
                  {hasSubNav && isExpanded && (
                    <ul
                      aria-label={`${item.label} sub-navigation`}
                      className='absolute right-0 mt-1.5 min-w-[8rem] overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'
                    >
                      {item.child?.map((subItem, subIndex) => (
                        <li
                          key={`subitem-${subIndex}`}
                          className='m-0.5 block'
                        >
                          <NavLink
                            {...subItem}
                            activeClassName='text-blue-600 bg-accent'
                            className='hover:bg-accent hover:text-accent-foreground focus:text-accent-foreground block select-none truncate rounded-md p-3 pr-6 text-left text-base font-semibold leading-none no-underline transition-colors focus:bg-zinc-300 focus:outline-1 focus:outline-offset-0'
                            tabIndex={0}
                          >
                            {subItem.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* -----------------------------------
            Desktop Right-side Utilities
            ------------------------------------ */}
        <div className='header-end hidden h-full items-center lg:flex'>
          {/* Search Icon + Overlay Form */}
          <div className='squiggly-link mr-2'>
            <div
              ref={searchTriggerRef}
              onClick={toggleSearch}
              onKeyDown={handleSearchKeyDown}
              role='button'
              aria-expanded={isSearchVisible}
              aria-controls='search-form'
              aria-label={isSearchVisible ? 'Close search' : 'Open search'}
              tabIndex={0}
              className='group relative inline-flex h-auto cursor-pointer items-center justify-center whitespace-nowrap p-2 text-base font-semibold text-black focus:outline-1 focus:outline-offset-0 lg:text-lg xl:p-2.5 xl:pb-1.5 xl:pt-1 xl:text-xl'
            >
              <Search className='h-5 w-5' />
              <div className='sr-only'>Search</div>
            </div>
          </div>

          {/* Tickets Link */}
          <div className='ml-1 hidden h-full lg:ml-2 lg:block xl:ml-4'>
            <GatsbyLink
              to={getLanguagePageLink('tickets')}
              className="ease-bounce after:bg-stubbottom focus:after:bg-stubbottom-dark group relative flex h-full items-center whitespace-nowrap bg-yellow-500 px-6 py-4 transition-all duration-200 after:absolute after:bottom-[-7px] after:left-0 after:right-0 after:h-[7px] after:w-full after:content-[''] hover:h-[110%] hover:pb-8 focus:h-[110%] focus:bg-blue-950 focus:pb-8 focus:text-white focus:outline-1 focus:outline-offset-1 xl:px-8"
            >
              <span className='text-xl font-semibold transition-colors duration-200 xl:text-2xl'>
                tickets
              </span>
            </GatsbyLink>
          </div>
        </div>

        {/* -----------------------------------
            Mobile Section
            ------------------------------------ */}
        <div className='flex h-full items-center lg:hidden'>
          {/* Mobile tickets link */}
          <RendedSmallTicketButton />

          {/* Mobile menu toggle button */}
          <Sheet
            open={open}
            onOpenChange={setOpen}
          >
            <RendedSheetTrigger />

            {/* Mobile menu content */}
            {open && (
              <SheetContent
                side='top'
                className={`bottom-0 top-0 -mr-1 w-full overflow-y-auto px-0 pb-5 pt-[75px] sm:max-w-full md:px-0 lg:hidden ${open && timeoutCompleted && `z-[150]`}`}
                overlayClassName='top-0 z-[120]'
                isShowCloseButton={false}
              >
                {/* Sticky header replication inside the sheet */}
                <div
                  className={`${open && timeoutCompleted ? 'fixed' : 'hidden'} left-0 right-0 top-0 z-[150] mx-auto flex h-[75px] max-w-screen-xl flex-wrap items-center justify-between bg-white px-5 py-5 md:pl-6 md:pr-6`}
                >
                  <LogoLink className='mt-1 focus:outline-offset-2 max-[400px]:h-[15px] sm:h-6 md:h-[26px] lg:h-[22px] xl:h-[26px]' />
                  <div className='flex h-full items-center'>
                    <RendedSmallTicketButton />
                    <RendedSheetTrigger />
                  </div>
                </div>

                {/* Mobile Accordion Menu */}
                <div className='flex flex-col space-y-3'>
                  <BaseAccordion
                    type='single'
                    collapsible
                    className='w-full'
                  >
                    {/* Render each menu item in accordion */}
                    {items &&
                      items.map((item, index) => {
                        const hasChild = item.child?.length;
                        return (
                          <AccordionItem
                            className='border-0'
                            value={`item-${index.toFixed()}`}
                            key={`accordion-item-${index}`}
                          >
                            {hasChild ? (
                              <>
                                <div className='flex items-center justify-between px-3 md:px-8'>
                                  <NavLink
                                    {...item}
                                    activeClassName='text-blue-600'
                                    className='m-0 w-full p-4 text-xl font-semibold leading-7 text-black hover:text-blue-800 hover:no-underline'
                                  >
                                    {item.label}
                                  </NavLink>
                                  <AccordionTrigger
                                    className='transition-transform duration-200 hover:no-underline data-[state=closed]:rotate-90 data-[state=open]:-rotate-90'
                                    aria-label={`Toggle ${item.label} submenu`}
                                  />
                                </div>
                                <AccordionContent>
                                  {item.child?.map((subItem, subIndex) => (
                                    <div
                                      className='bg-zinc-100 px-7 py-4 md:px-12'
                                      key={subIndex}
                                    >
                                      <NavLink
                                        {...subItem}
                                        activeClassName='text-blue-600'
                                        className='m-0 p-0 text-xl font-light leading-7 hover:text-blue-800 hover:no-underline'
                                      >
                                        {subItem.label}
                                      </NavLink>
                                    </div>
                                  ))}
                                </AccordionContent>
                              </>
                            ) : (
                              // Single-level item
                              <NavLink
                                key={`nav-item-mobile-${index}`}
                                {...item}
                                activeClassName='text-blue-600'
                                className='m-0 block p-0 px-7 py-4 text-xl font-semibold leading-7 hover:text-blue-800 md:px-12'
                              >
                                {item.label}
                              </NavLink>
                            )}
                          </AccordionItem>
                        );
                      })}

                    {/* Language selector in accordion */}
                    <AccordionItem
                      className='hidden border-0'
                      value='item-language'
                      key='accordion-item-language'
                    >
                      <AccordionTrigger className='px-7 hover:no-underline md:px-12 [&[data-state=closed]>svg]:rotate-90 [&[data-state=open]>svg]:-rotate-90 [&[data-state]>svg]:h-5 [&[data-state]>svg]:w-5'>
                        <div className='flex items-center gap-1'>
                          {(() => {
                            const selectedSupportedLang = supportedLanguages[selectedLanguage];
                            const langName = selectedSupportedLang
                              ? selectedSupportedLang.name
                              : selectedLanguage;
                            const FlagComponent = selectedSupportedLang
                              ? selectedSupportedLang.flagComponent
                              : AmericanFlag;

                            return (
                              <>
                                <FlagComponent />
                                <span className='m-0 p-0 px-0 text-xl font-semibold leading-7 text-black hover:text-blue-800 hover:no-underline'>
                                  {langName}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        {languages.map((langCode, index) => {
                          const supportedLang = supportedLanguages[langCode];
                          const name = supportedLang ? supportedLang.name : langCode;
                          const FlagComponent = supportedLang
                            ? supportedLang.flagComponent
                            : AmericanFlag;

                          return (
                            <div
                              className='bg-zinc-100 px-7 py-4 md:px-12'
                              key={index}
                            >
                              {getHrefByLanguageCode(langCode) ? (
                                <GatsbyLink
                                  to={getHrefByLanguageCode(langCode) as string}
                                  activeClassName='text-blue-600 bg-accent'
                                  className='m-0 flex items-center p-0 text-xl font-light leading-7 hover:text-blue-800 hover:no-underline'
                                >
                                  <FlagComponent /> {name}
                                </GatsbyLink>
                              ) : (
                                <span
                                  onClick={handleLanguageChange}
                                  className='m-0 flex cursor-pointer items-center p-0 text-xl font-light leading-7 hover:text-blue-800'
                                >
                                  <FlagComponent /> {name}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Search form in accordion */}
                    <AccordionItem
                      className='border-0'
                      value='item-search'
                      key='accordion-item-search'
                    >
                      <div className='px-7 pb-2 pt-5 md:px-12'>
                        <form
                          className='relative mx-auto flex w-full max-w-sm items-center'
                          onSubmit={handleSearch}
                        >
                          <input
                            ref={searchInputRef}
                            onKeyDown={handleSearchInputKeyDown}
                            className='w-full rounded border border-zinc-300 px-5 py-4 text-base text-black focus:outline-1 focus:outline-offset-0'
                            inputMode='text'
                            placeholder='Explore all the options'
                            name='search'
                          />
                          <button
                            className='absolute right-5'
                            aria-label='Search'
                            type='submit'
                          >
                            <Search className='h-5 w-5' />
                          </button>
                        </form>
                      </div>
                    </AccordionItem>
                  </BaseAccordion>

                  {/* Get tickets button in mobile menu */}
                  <div className='mx-auto w-full px-7 text-center'>
                    <GatsbyLink
                      to='/tickets'
                      className='mx-auto mt-0 inline-flex h-10 w-full max-w-sm items-center justify-center rounded-full bg-yellow-500 px-4 py-6 pb-3 pt-3 text-base font-semibold leading-5 text-black hover:bg-blue-950 hover:text-white focus:bg-blue-950 focus:text-white focus:outline-1 focus:outline-offset-0'
                    >
                      get tickets
                    </GatsbyLink>
                  </div>
                </div>
              </SheetContent>
            )}
          </Sheet>
        </div>
      </div>
      <div
        className={`relative left-0 right-0 top-[100%] z-10 transition-all duration-300 md:px-12 ${
          isSearchVisible ? 'block' : 'hidden'
        }`}
      >
        <div className='container py-5'>
          <form
            className='relative mx-auto flex w-full items-center'
            onSubmit={handleSearch}
          >
            <input
              ref={searchInputRef}
              className='w-full rounded border border-zinc-300 px-5 py-4 text-base text-black focus:outline-1 focus:outline-offset-0'
              inputMode='text'
              placeholder='Explore all the options'
              name='search'
              onKeyDown={handleSearchInputKeyDown}
            />
            <button
              className='absolute right-5'
              aria-label='Search'
              type='submit'
            >
              <Search className='h-5 w-5' />
            </button>
          </form>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
